export const MONTHS_12 = Array.from({ length: 12 }, () => 0);

export const DEFAULT_KPIS = {
  dealName: "",
  location: "",
  address: "",
  photoUrl: "",
  propertyType: "Boutique",
  starRating: 4,
  currency: "EUR",
  gfaSqm: 0,
  purchase: { price: 0 },
  rooms: { total: 0 },
  roomMix: [],
  facilities: {
    restaurant: false,
    bar: false,
    roomService: false,
    spa: false,
    parking: false,
    meetingEvents: false,
    pool: false,
    gym: false,
  },
  budget: {
    transferTax: 0,
    dealCosts: 0,
    construction: 0,
    ffoeOse: 0,
    profFees: 0,
    planningFees: 0,
    devFee: 0,
    insuranceAdmin: 0,
    other: 0,
    preopening: 0,
    contingencyPct: 10,
  },
  roomRev: {
    adr: [140, 140, 145, 150, 155, 160, 165, 165, 160, 155, 145, 140],
    occ: [55, 60, 65, 70, 75, 80, 85, 85, 80, 75, 65, 60],
  },
  fnb: {
    avgGuests: 1.8,
    capture: {
      breakfast: 80,
      lunch: 40,
      dinner: 60,
      bar: 45,
    },
    check: {
      guest: {
        breakfast: 12,
        lunch: 18,
        dinner: 35,
        bar: 15,
      },
      external: {
        breakfast: 15,
        lunch: 22,
        dinner: 45,
        bar: 18,
      },
    },
    external: {
      perDay: {
        breakfast: 5,
        lunch: 15,
        dinner: 20,
        bar: 25,
      },
    },
  },
  otherRev: {
    wellness: {
      treatmentsPerDay: 4,
      avgPrice: 70,
    },
    other: {
      mode: "pctRooms",
      value: 5,
    }, // "pctRooms" | "monthlyFixed"
  },
  opex: {
    roomsCommission: 15,
    guestSuppliesCleaning: 8,
    cogs: 30,
    meetingsEvents: 2,
    wellnessOther: 120000,
    otherDirect: 2000,
    adminGeneralOther: 4,
    techSubscriptions: 1200,
    salesMarketingOther: 3,
    maintenanceOther: 2,
    utilities: 3,
    mgmtFee: 3,
    propertyTaxes: 1,
    insurance: 0.5,
    rent: 0,
  },

  payroll: {
    mode: "simple",
    marketId: "",
    serviceStyle: "Limited Service",
    socialChargesPercent: 25,
    positions: [],
    rooms: 0,
    totals: {
      totalFTE: 0,
      annualCost: 0,
      monthlyCost: 0,
      avgCostPerFTE: 0,
    },
  },

  // RAMP & MACRO SETTINGS
  ramp: {
    // Opening and stabilization
    openingMonth: 1, // Month of opening (1-12)
    rampMonths: 12, // Months to reach stabilized occupancy
    stabilizedOccupancy: 70, // Target stabilized occupancy %

    // Growth rates
    inflationRate: 2, // Annual inflation %
    revenueGrowthY2: 3, // Year 2 revenue growth %
    revenueGrowthY3Plus: 2, // Year 3+ revenue growth %

    // Expense growth
    expenseInflationRate: 2.5, // Operating expense inflation %
    payrollGrowthRate: 3, // Annual payroll growth %

    // Seasonality override
    useCustomSeasonality: false,
    customSeasonalityFactors: [...MONTHS_12.map(() => 100)], // 100 = no adjustment
  },

  // FINANCING STRUCTURE
  financing: {
    // Debt parameters
    debtToEquityRatio: 60, // Debt as % of total investment
    interestRate: 5, // Annual interest rate %
    loanTermYears: 20, // Loan amortization period
    interestOnlyMonths: 0, // Interest-only period

    // Covenants and requirements
    dscrTarget: 1.25, // Debt Service Coverage Ratio target
    minimumEquity: 40, // Minimum equity requirement %

    // Fees and costs
    arrangementFeePct: 1, // Loan arrangement fee %
    exitRefinancingCostPct: 1, // Refinancing cost at exit %

    // Construction loan (if applicable)
    constructionLoanRate: 6, // Construction period interest %
    constructionDrawSchedule: "linear" as "linear" | "s-curve" | "custom",
  },

  // EXIT STRATEGY
  exit: {
    // Timing
    holdPeriodYears: 5, // Investment hold period

    // Valuation methods
    valuationMethod: "capRate" as "capRate" | "multiple" | "dcf",
    exitCapRate: 7, // Exit cap rate %
    exitNOIMultiple: 12, // Alternative: NOI multiple

    // Transaction costs
    sellingCostsPct: 2, // Broker fees and selling costs %
    taxOnGainPct: 20, // Capital gains tax %

    // Market assumptions
    marketGrowthRate: 2, // Annual market value growth %
    terminalGrowthRate: 2, // Terminal value growth rate (for DCF)

    // Risk adjustments
    discountRate: 10, // Discount rate for DCF %
    probabilityOfSale: 100, // Probability of successful exit %
  },
} as const;

export type Kpis = typeof DEFAULT_KPIS & {
  // roomRev contains the INPUT monthly arrays (from template)
  roomRev?: {
    adr: number[]; // 12 monthly ADR values
    occ: number[]; // 12 monthly occupancy % values
  };

  // roomRevenue contains COMPUTED values (calculated from roomRev)
  roomRevenue?: {
    monthly?: {
      adr: number[]; // Same as roomRev.adr
      occ: number[]; // Same as roomRev.occ
    };
    totals?: {
      avgADR?: number; // Weighted average ADR
      avgOccPct?: number; // Average occupancy %
      avgRevPAR?: number; // Average RevPAR
      roomsRevenue?: number; // Total annual revenue
      roomsAvailable?: number;
      roomsSold?: number;
    };
    preset?: string | null; // Seasonality preset used
  };

  payroll?: {
    mode: string;
    marketId?: string;
    positions?: any[];
    [key: string]: any;
  };
  investmentBudget?: {
    netPurchasePrice?: number;
    reTransferTax?: number;
    dealCosts?: number;
    constructionCosts?: number;
    ffeOse?: number;
    professionalFees?: number;
    planningCharges?: number;
    developmentFee?: number;
    insuranceAdmin?: number;
    otherDev?: number;
    preOpening?: number;
    contingencyPct?: number;
    grandTotal?: number;
  };

  // Computed KPIs
  totalInvestment?: number;
  costPerRoom?: number;
  costPerSqm?: number;
  avgADR?: number;
  avgOccupancy?: number;
  avgRevPAR?: number;
  totalRoomsRevenue?: number;
  totalPayrollAnnual?: number;
};
